import type { NavigateFunction } from 'react-router-dom';
import { fetchVysorContext, type VysorAppCatalogItem, type VysorIdpOption } from './vysorCatalog';
import { canRunVysorWizard, SETUP_APP_FLOW, ADD_INSTANCE_SUBFLOW_FIELDS, SSO_CONFIGURE_FLOW, type WizardFlow } from './vysorFlows';
import type { VysorWizardAction, VysorWizardContext, WizardState, WizardAdvanceResult } from './vysorWizardEngine';
import {
  advanceInstanceSubWizard,
  advanceSsoWizardAfterProtocolPrefill,
  buildWizardListPickerMessage,
  extractMetadataCandidate,
  resumeWizardAfterInstanceCreated,
  rewindWizardToApplicationStep,
} from './vysorWizardEngine';
import {
  applyExtractedSsoToCollected,
  isMetadataXmlContent,
  resolveSsoReferenceInput,
} from './vysorMetadata';
import { submitVysorInstanceWizard, wizardSubmitErrorMessage } from './vysorWizardSubmit';

export function buildVysorWizardContext(
  userRole: string | undefined,
  configuredIdps: VysorIdpOption[],
): VysorWizardContext {
  return {
    configuredIdps,
    canSetupApp: canRunVysorWizard(userRole, 'setup-app'),
  };
}

/** After Setup application API succeeds — stay in VYSOR and chain into environment + SSO. */
export function beginPostSetupAppInstanceChain(
  app: { id: string; name: string },
  handlers: {
    addBotMessage: (content: string) => void;
    setWizard: (wizard: WizardState | null) => void;
    applyWizardOptions: (
      field: import('./vysorFlows').WizardField,
      collected: Record<string, string>,
      options?: { withQuestion?: boolean },
    ) => void;
  },
): void {
  const collected = {
    application_id: app.id,
    application_name: app.name,
  };
  const firstField = ADD_INSTANCE_SUBFLOW_FIELDS[0];
  handlers.setWizard({
    flow: SSO_CONFIGURE_FLOW,
    stepIndex: 1,
    collected,
    subFlow: 'add-instance',
    subFlowStepIndex: 0,
    listPage: 1,
  });
  handlers.addBotMessage(
    `**${app.name}** is registered in your catalog.\n\n` +
      `Next, let's add an environment, then configure SSO for it — right here in chat.\n\n` +
      firstField.question,
  );
  handlers.applyWizardOptions(firstField, collected);
}

export interface GuidedActionHandlers {
  onRefreshCatalog: () => Promise<VysorAppCatalogItem[]>;
  onStartWizard: (flow: WizardFlow) => void;
  /** Prefer AI Mode conversational entry for setup-app (and similar). */
  onAiGoal?: (flowId: WizardFlow['id']) => void | Promise<void>;
  onNavigate: (path: string) => void;
  addBotMessage: (content: string) => void;
  setWizard: (wizard: WizardState | null) => void;
  getWizard: () => WizardState | null;
  applyWizardOptions: (
    field: import('./vysorFlows').WizardField,
    collected: Record<string, string>,
    options?: { withQuestion?: boolean },
  ) => void;
}

export async function handleVysorGuidedAction(
  action: VysorWizardAction,
  handlers: GuidedActionHandlers,
  collectedOverride?: Record<string, string>,
): Promise<void> {
  const wizard = handlers.getWizard();
  const collected = { ...(wizard?.collected || {}), ...(collectedOverride || {}) };

  switch (action) {
    case 'setup_app':
      // Prefer AI Mode (same as quick action) over rigid local wizard steps.
      if (handlers.onAiGoal) {
        await handlers.onAiGoal('setup-app');
        return;
      }
      handlers.addBotMessage(
        "Let's register a new application — ask me anything as we go, or answer the short steps below.",
      );
      handlers.onStartWizard(SETUP_APP_FLOW);
      return;

    case 'browse_apps':
      handlers.addBotMessage(
        'Stay in VYSOR — use **Register new application** (admins) or **Refresh catalog** after your team adds apps.',
      );
      await handlers.onRefreshCatalog();
      if (wizard) {
        const field = wizard.flow.fields[wizard.stepIndex];
        handlers.applyWizardOptions(field, wizard.collected, { withQuestion: true });
      }
      return;

    case 'add_instance': {
      const appId = collected.application_id;
      if (!appId) {
        handlers.addBotMessage('Pick an application first, then choose **Add environment now**.');
        return;
      }
      if (!wizard) return;

      // Prefer collected from the guided-action result (may include name/env hints from free text).
      const merged = { ...wizard.collected, ...collected };
      let stepIndex = 0;
      while (
        stepIndex < ADD_INSTANCE_SUBFLOW_FIELDS.length
        && Boolean(merged[ADD_INSTANCE_SUBFLOW_FIELDS[stepIndex].key]?.trim())
      ) {
        stepIndex += 1;
      }
      // Description is optional — if name+env are set, land on description (or finish prompt).
      if (stepIndex >= ADD_INSTANCE_SUBFLOW_FIELDS.length) {
        stepIndex = ADD_INSTANCE_SUBFLOW_FIELDS.length - 1;
      }

      const firstField = ADD_INSTANCE_SUBFLOW_FIELDS[stepIndex];
      handlers.setWizard({
        ...wizard,
        collected: merged,
        subFlow: 'add-instance',
        subFlowStepIndex: stepIndex,
        listPage: 1,
        listFilter: undefined,
        awaitingListFilter: false,
      });

      const appLabel = merged.application_name || 'your app';
      const hintedName = merged.new_instance_name?.trim();
      const hintedEnv = merged.new_instance_environment?.trim();
      if (hintedName && hintedEnv && stepIndex > 0) {
        handlers.addBotMessage(
          `Got it — I'll create **${hintedName}** (${hintedEnv}) for **${appLabel}**. ` +
            `Nothing resets; we're just adding the missing environment.\n\n${firstField.question}`,
        );
      } else if (hintedName) {
        handlers.addBotMessage(
          `Got it — creating environment **${hintedName}** for **${appLabel}**.\n\n${firstField.question}`,
        );
      } else {
        handlers.addBotMessage(
          `Of course — let's add an environment for **${appLabel}** right here. ` +
            `Tell me the name (e.g. Staging) and type, then I'll create it and continue SSO.\n\n${firstField.question}`,
        );
      }
      handlers.applyWizardOptions(firstField, merged);
      return;
    }

    case 'change_application': {
      if (!wizard) return;
      const rewound = rewindWizardToApplicationStep(wizard);
      handlers.setWizard(rewound);
      const appField = rewound.flow.fields[rewound.stepIndex];
      handlers.addBotMessage("No problem — let's pick a different application.");
      handlers.applyWizardOptions(appField, rewound.collected, { withQuestion: true });
      return;
    }

    case 'refresh_catalog': {
      handlers.addBotMessage('Refreshing your catalog…');
      const apps = await handlers.onRefreshCatalog();
      if (!wizard) {
        handlers.addBotMessage(`Catalog updated — **${apps.length}** application(s) available.`);
        return;
      }
      const field = wizard.flow.fields[wizard.stepIndex];
      const appCount = apps.length;
      const instCount = collected.application_id
        ? apps.find(a => a.id === collected.application_id)?.instances.length ?? 0
        : 0;
      if (field.type === 'application') {
        const refreshed = {
          ...wizard,
          listPage: 1,
          listFilter: wizard.listFilter ?? wizard.catalogFilter,
          awaitingListFilter: false,
          catalogPage: 1,
          awaitingCatalogFilter: false,
        };
        handlers.setWizard(refreshed);
        handlers.addBotMessage(
          appCount
            ? `Catalog refreshed.\n\n${buildWizardListPickerMessage(field, apps, wizard.collected, { canSetupApp: true }, 1, refreshed.listFilter)}`
            : 'Still no applications — register one in Applications or run Setup application.',
        );
        handlers.applyWizardOptions(field, wizard.collected);
        return;
      }
      let statusMsg = 'Catalog refreshed.';
      if (field.type === 'instance') {
        const refreshed = {
          ...wizard,
          listPage: 1,
          awaitingListFilter: false,
          catalogPage: 1,
          awaitingCatalogFilter: false,
        };
        handlers.setWizard(refreshed);
        statusMsg = instCount
          ? `Catalog refreshed.\n\n${buildWizardListPickerMessage(field, apps, wizard.collected, {}, 1, refreshed.listFilter ?? refreshed.catalogFilter)}`
          : `No environments yet for **${collected.application_name || 'this app'}** — choose **Add environment now** to create one here.`;
        handlers.addBotMessage(statusMsg);
        handlers.applyWizardOptions(field, wizard.collected, { withQuestion: !instCount });
        return;
      }
      handlers.addBotMessage(statusMsg);
      handlers.applyWizardOptions(field, wizard.collected, { withQuestion: true });
      return;
    }

    default:
      return;
  }
}

export interface InstanceSubFlowHandlers {
  setWizard: (wizard: WizardState | null) => void;
  addBotMessage: (content: string) => void;
  applyWizardOptions: (
    field: import('./vysorFlows').WizardField,
    collected: Record<string, string>,
    options?: { withQuestion?: boolean },
  ) => void;
  onRefreshCatalog: () => Promise<VysorAppCatalogItem[]>;
  setBusy?: (busy: boolean) => void;
}

/** Handle one step of the inline add-environment sub-wizard. Returns true if consumed. */
export async function processInstanceSubFlowStep(
  wizard: WizardState,
  rawAnswer: string,
  optionId: string | undefined,
  optionValue: string | undefined,
  handlers: InstanceSubFlowHandlers,
): Promise<boolean> {
  if (wizard.subFlow !== 'add-instance' || wizard.subFlowStepIndex === undefined) {
    return false;
  }

  const result = advanceInstanceSubWizard(
    wizard.subFlowStepIndex,
    wizard.collected,
    rawAnswer,
    optionId,
    optionValue,
  );

  if (result.kind === 'cancelled') {
    const instanceField = wizard.flow.fields.find(f => f.type === 'instance');
    if (!instanceField) {
      handlers.setWizard(null);
      handlers.addBotMessage('Cancelled.');
      return true;
    }
    const instanceStepIndex = wizard.flow.fields.indexOf(instanceField);
    handlers.setWizard({
      flow: wizard.flow,
      stepIndex: instanceStepIndex,
      collected: wizard.collected,
    });
    handlers.addBotMessage('Cancelled adding an environment. Choose an option below.');
    handlers.applyWizardOptions(instanceField, wizard.collected, { withQuestion: true });
    return true;
  }

  if (result.kind === 'invalid') {
    const field = ADD_INSTANCE_SUBFLOW_FIELDS[wizard.subFlowStepIndex];
    handlers.addBotMessage(result.invalidHint || 'Please try again.');
    handlers.applyWizardOptions(field, wizard.collected, { withQuestion: true });
    return true;
  }

  if (result.kind === 'subflow-next') {
    handlers.setWizard({
      ...wizard,
      collected: result.collected,
      subFlowStepIndex: result.nextStepIndex,
    });
    const hint = result.nextField.hint ? `\n\n_${result.nextField.hint}_` : '';
    handlers.addBotMessage(`${result.nextField.question}${hint}`);
    handlers.applyWizardOptions(result.nextField, result.collected);
    return true;
  }

  if (result.kind === 'subflow-complete') {
    handlers.setBusy?.(true);
    try {
      const created = await submitVysorInstanceWizard(result.collected);
      await handlers.onRefreshCatalog();
      const { wizard: resumed, nextField } = resumeWizardAfterInstanceCreated(wizard, created);
      if (!nextField) {
        handlers.setWizard(null);
        handlers.addBotMessage(`Environment **${created.name} (${created.environment})** was created.`);
        return true;
      }
      handlers.setWizard(resumed);
      handlers.addBotMessage(
        `Environment **${created.name} (${created.environment})** was created. Continuing SSO setup…`,
      );
      const hint = nextField.hint ? `\n\n_${nextField.hint}_` : '';
      handlers.addBotMessage(`${nextField.question}${hint}`);
      handlers.applyWizardOptions(nextField, resumed.collected);
    } catch (err) {
      handlers.addBotMessage(
        `Could not create environment: ${wizardSubmitErrorMessage(err, 'Please try again.')}`,
      );
      const field = ADD_INSTANCE_SUBFLOW_FIELDS[wizard.subFlowStepIndex];
      handlers.applyWizardOptions(field, result.collected, { withQuestion: true });
      handlers.setWizard({
        ...wizard,
        collected: result.collected,
      });
    } finally {
      handlers.setBusy?.(false);
    }
    return true;
  }

  return false;
}

export type PreparedSsoProtocolInput =
  | { mode: 'default'; rawAnswer: string; optionId?: string; optionValue?: string }
  | { mode: 'advanced'; result: WizardAdvanceResult; botMessages: string[] }
  | { mode: 'abort'; botMessages: string[] };

/** On SSO protocol step: fetch metadata URLs, read uploaded SAML XML, before normal wizard resolution. */
export async function prepareSsoProtocolWizardInput(
  wizard: WizardState,
  rawAnswer: string,
  optionId: string | undefined,
  optionValue: string | undefined,
  attachmentText?: string,
): Promise<PreparedSsoProtocolInput> {
  const field = wizard.flow.fields[wizard.stepIndex];
  if (field?.key !== 'sso_type' || optionValue) {
    return { mode: 'default', rawAnswer, optionId, optionValue };
  }

  const attachment = attachmentText?.trim();
  const attachmentMeta = attachment ? extractMetadataCandidate(attachment) : null;
  if (attachmentMeta && isMetadataXmlContent(attachmentMeta)) {
    return { mode: 'default', rawAnswer: attachmentMeta, optionId, optionValue };
  }

  const url = extractMetadataCandidate(rawAnswer);
  if (!url || !/^https?:\/\//i.test(url) || isMetadataXmlContent(url)) {
    return { mode: 'default', rawAnswer, optionId, optionValue };
  }

  const botMessages: string[] = [];
  const enriched = await enrichWizardMetadataField(wizard.collected, url);
  if (enriched.summary) botMessages.push(enriched.summary);
  if (enriched.error) {
    botMessages.push(enriched.error);
    return { mode: 'abort', botMessages };
  }

  const collected = { ...wizard.collected, ...enriched.collected, sso_type: 'SAML' };
  return {
    mode: 'advanced',
    botMessages,
    result: advanceSsoWizardAfterProtocolPrefill(wizard, collected, 'SAML (from metadata URL)'),
  };
}

/** When wizard metadata step receives a URL, XML, discovery JSON, or guide text — enrich collected. */
export async function enrichWizardMetadataField(
  collected: Record<string, string>,
  rawValue: string,
): Promise<{ collected: Record<string, string>; summary?: string; error?: string; abortAdvance?: boolean }> {
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return { collected: { ...collected, metadata_or_notes: trimmed } };
  }

  if (/^http:\/\//i.test(trimmed)) {
    return {
      collected: { ...collected, metadata_or_notes: trimmed },
      error: 'Only HTTPS URLs are allowed. Please use an `https://` URL, paste XML/JSON, or choose **Skip**.',
      abortAdvance: true,
    };
  }

  const { extracted, error } = await resolveSsoReferenceInput(trimmed);
  const next = applyExtractedSsoToCollected(collected, extracted, trimmed);

  const captured =
    !!(
      next.metadata_entity_id
      || next.metadata_acs_url
      || next.oidc_discovery_endpoint
      || next.redirect_uris
      || next.metadata_content
    );

  // Soft error: keep going with notes so wizard can still submit
  if (error && !captured) {
    return {
      collected: next,
      error,
      // Do not hard-abort when we at least saved the URL/notes — user can continue
      abortAdvance: /^http:\/\//i.test(trimmed),
    };
  }

  const summary = extracted.summaryLines.length
    ? extracted.summaryLines.join('\n')
    : captured
      ? 'I captured details from your reference and will include them in the SSO configuration.'
      : undefined;

  return {
    collected: next,
    summary,
    error: error && captured ? undefined : error,
  };
}
